if (!customElements.get('custom-carousel')) {
    class CustomCarousel extends window.HTMLElement {
        constructor() {
            super();
            this.selectors = {
                loop: "data-loop",
                scrollCount: "[data-scroll-num]",
                dragFree: "data-drag-free",
                align: "data-align",
                controls: "data-controls",
                autoplay: "data-autoplay",
                autoplayInterval: "data-autoplay-interval",
                carouselContainer: ".carousel-container",
                progressBar: ".progress-bar",
                dotsContainer: ".dots-container"
            };

            this.autoplayPlugin = null;
        }

        connectedCallback() {
            const loop = this.hasAttribute(this.selectors.loop);
            const scrollCount = this.getAttribute(this.selectors.scrollCount) ?? 1;
            const dragFree = this.hasAttribute(this.selectors.dragFree);
            const controls = this.hasAttribute(this.selectors.controls);
            const align = this.getAttribute(this.selectors.align) ?? 'start';
            const autoplay = this.hasAttribute(this.selectors.autoplay);
            const autoplayInterval = parseInt(this.getAttribute(this.selectors.autoplayInterval)) || 5000;

            const carouselContainer = this.querySelector(this.selectors.carouselContainer);

            const plugins = [];
            if (autoplay) {
                this.autoplayPlugin = EmblaCarouselAutoplay({ delay: autoplayInterval });
                plugins.push(this.autoplayPlugin);
            }

            this.embla = EmblaCarousel(carouselContainer, {
                loop: loop,
                align: align,
                dragFree: dragFree,
                skipSnaps: true,
                slidesToScroll: parseInt(scrollCount),
            }, plugins);

            if (controls) {
                this.setupControls();
            }

            this.setupDots();
            this.setupProgressBar();

            this.embla.on('select', () => {
                this.updateProgressBar();
                this.updateDots();
            });

            this.embla.on('scroll', () => {
                this.updateProgressBar();
            });
        }

        disconnectedCallback() {
            if (this.embla) {
                this.embla.destroy();
                this.embla = null;
            }
        }

        setupControls() {
            const prevBtnNodes = Array.from(this.querySelectorAll(".embla__button--prev"));
            const nextBtnNodes = Array.from(this.querySelectorAll(".embla__button--next"));

            prevBtnNodes.forEach((prevBtnNode, index) => {
                const nextBtnNode = nextBtnNodes[index];
                if (nextBtnNode) {
                    const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
                        this.embla,
                        prevBtnNode,
                        nextBtnNode
                    );
                    this.embla.on('destroy', removePrevNextBtnsClickHandlers);
                }
            });
        }

        setupDots() {
            const dotsContainer = this.querySelector(this.selectors.dotsContainer);
            if (!dotsContainer) return;

            const slides = this.embla.slideNodes();
            dotsContainer.innerHTML = ""; // Clear any existing dots

            slides.forEach((_, index) => {
                const dot = document.createElement("button");
                dot.className = "dot";
                dot.addEventListener("click", () => this.embla.scrollTo(index));
                dotsContainer.appendChild(dot);
            });

            this.updateDots();
        }

        updateDots() {
            const dotsContainer = this.querySelector(this.selectors.dotsContainer);
            if (!dotsContainer) return;

            const selectedIndex = this.embla.selectedScrollSnap();
            dotsContainer.querySelectorAll(".dot").forEach((dot, index) => {
                dot.classList.toggle("active", index === selectedIndex);
            });
        }

        setupProgressBar() {
            this.updateProgressBar();
        }

        updateProgressBar() {
            const progressBars = this.querySelectorAll(this.selectors.progressBar);
            if (progressBars.length === 0) return;

            setTimeout(() => {
                const totalSlides = this.embla.slideNodes().length;
                const scrollProgress = this.embla.scrollProgress();

                const fullyVisibleSlides = this.embla
                    .slidesInView(true)
                    .length - 1;

                const initialProgress = (fullyVisibleSlides / totalSlides) * 100;
                const currentProgress = scrollProgress * (100 - initialProgress) + initialProgress;

                progressBars.forEach(progressBar => {
                    progressBar.style.width = `${currentProgress}%`;
                });
            }, 300);
        }

        addTogglePrevNextBtnsActive(emblaApi, prevBtn, nextBtn) {
            const togglePrevNextBtnsState = () => {
                if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled');
                else prevBtn.setAttribute('disabled', 'disabled');

                if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled');
                else nextBtn.setAttribute('disabled', 'disabled');
            };

            emblaApi
                .on('select', togglePrevNextBtnsState)
                .on('init', togglePrevNextBtnsState)
                .on('reInit', togglePrevNextBtnsState);

            return () => {
                prevBtn.removeAttribute('disabled');
                nextBtn.removeAttribute('disabled');
            };
        }

        addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
            const scrollPrev = () => {
                emblaApi.scrollPrev();
            };
            const scrollNext = () => {
                emblaApi.scrollNext();
            };
            prevBtn.addEventListener('click', scrollPrev, false);
            nextBtn.addEventListener('click', scrollNext, false);

            const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
                emblaApi,
                prevBtn,
                nextBtn
            );

            return () => {
                removeTogglePrevNextBtnsActive();
                prevBtn.removeEventListener('click', scrollPrev, false);
                nextBtn.removeEventListener('click', scrollNext, false);
            };
        }
    }

    window.customElements.define("custom-carousel", CustomCarousel);
}